/**
 * @param flightId セグメントIDが項目名となる
 * @param isAvailable
機内食の申し込み可否<br/>
以下の全ての条件を満たす場合にtrue<br/>
1.オープン便でないNHグループ便名NHグループ運航国際便である<br/>
2.出発済みでない<br/>
3.セグメントステータスが機内食申込対象セグメントステータス(設定ファイルに定義)に含まれる
* @param isExpired 受付時間内。出発まで設定ファイルで定めた時間以内の場合、true。
* @param travelerId 搭乗者IDが項目名となる
*/

/** New Interface Service [asw-inflight-meal]  */

export interface Meal {
  [flightId: string]: FlightMealInfo;
}

export interface FlightMealInfo {
  isAvailable?: boolean;
  isExpired?: boolean;
  [travelerId: string]: TravelerMealInfo | boolean | undefined;
}

export interface TravelerMealInfo {
  appliedMealList: Array<AppliedMealList>;
}
export interface AppliedMealList {
  code?: string;
  id?: string;
  statusCode?: string;
  type?: TravelerMealInfo.MealType;
  preOrderMealMessageID?: string;
  serviceStatus?: TravelerMealInfo.ServiceStatusEnum;
  price?: PriceMeal;
}
export interface PriceMeal {
  total?: number;
  currencyCode?: string;
}
export namespace TravelerMealInfo {
  export type UpdateCategory = (typeof UpdateCategory)[keyof typeof UpdateCategory];
  export const UpdateCategory = {
    CANCEL: 'cancel',
    REQUEST: 'request',
  } as const;
}

export namespace TravelerMealInfo {
  export type MealCode = (typeof MealCode)[keyof typeof MealCode];
  export const MealCode = {
    BBML: 'BBML',
    CHML: 'CHML',
    NTML: 'NTML',
  } as const;
}

export namespace TravelerMealInfo {
  export type MealType = (typeof MealType)[keyof typeof MealType];
  export const MealType = {
    SPECIAL: 'special',
    ANCILLARY: 'ancillary',
    PREORDER: 'preOrder',
    NOTMEAL: 'notMeal',
    NORMAL: 'normal',
  } as const;
}

export interface TravelerInFlightMealInfo {
  type?: string;
  cabin?: string;  
}

export namespace TravelerMealInfo {
  export type ServiceStatusEnum =
    | 'confirmed'
    | 'prebooked'
    | 'confirmedWithoutTsm'
    | 'requesting'
    | 'cancelled'
    | 'unavailable'
    | 'requested';
  export const ServiceStatusEnum = {
    Confirmed: 'confirmed' as ServiceStatusEnum,
    Prebooked: 'prebooked' as ServiceStatusEnum,
    ConfirmedWithoutTsm: 'confirmedWithoutTsm' as ServiceStatusEnum,
    Requesting: 'requesting' as ServiceStatusEnum,
    Cancelled: 'cancelled' as ServiceStatusEnum,
    Unavailable: 'unavailable' as ServiceStatusEnum,
    Requested: 'requested' as ServiceStatusEnum,
  };
}

/** New Interface Service [lounge-access]  */

export interface Lounge {
  [key: string]: SegmentLoungeMap;
}

export interface SegmentLoungeMap {
  isAvailable?: boolean;
  [key: string]: PassengerLoungeMap | boolean | undefined;
}

/**
 * @param code 有料ラウンジのSSRコード
 * @param statusCode 有料ラウンジのSSRステータスコード
 * @param serviceStatus 有料ラウンジのSSRステータスとAncillaryのEMDの状態に応じたサービスの申込状態
 * @param isMyLoungeRequested 空港ラウンジが申し込まれているか
 * @param isWaived 申し込み不要で利用できるかどうか
 * @param isAvailable 有料ラウンジの申込可否
 * @param prices 有料ラウンジの申込済みの金額情報
 * @param catalogue 有料ラウンジのサービスカタログ
 */
export interface PassengerLoungeMap {
  code?: string;
  id?: string;
  statusCode?: string;
  serviceStatus?: PassengerLoungeMap.ServiceStatusEnum;
  isMyLoungeRequested?: boolean;
  isWaived?: boolean;
  isAvailable?: boolean;
  prices?: LoungePriceInfo;
  catalogue?: Array<LoungeCatalogueItem>;
}

/**
 * @param total 総額
 * @param currencyCode 通貨コード
 */
export interface LoungePriceInfo {
  total?: number;
  currencyCode?: string;
}

/**
 * @param code SSRコード
 * @param quota 在庫数
 * @param prices 有料ラウンジの申込済みの金額情報
 */
export interface LoungeCatalogueItem {
  code?: string;
  quota?: string;
  prices?: LoungePriceInfo;
}

export namespace PassengerLoungeMap {
  export type ServiceStatusEnum =
    | 'confirmed'
    | 'prebooked'
    | 'confirmedWithoutTsm'
    | 'requesting'
    | 'cancelled'
    | 'requested';
  export const ServiceStatusEnum = {
    Confirmed: 'confirmed' as ServiceStatusEnum,
    Prebooked: 'prebooked' as ServiceStatusEnum,
    ConfirmedWithoutTsm: 'confirmedWithoutTsm' as ServiceStatusEnum,
    Requesting: 'requesting' as ServiceStatusEnum,
    Cancelled: 'cancelled' as ServiceStatusEnum,
    Requested: 'requested' as ServiceStatusEnum,
  };
}

export interface SegmentMyCarValetMap {
  [passengerId: string]: PassengerMyCarValetMap;
}
export interface PassengerMyCarValetMap {
  isRequested: boolean;
}

/** New Interface Service [asw-my-car-valet]  */
/** マイカーバレー
 * @param flightId セグメントIDが項目名となる
 * @param passengerId 搭乗者IDが項目名となる
 */
export interface MyCarValet {
  [flightId: string]: SegmentMyCarValetMap;
}

export interface SegmentJuniorPilotMap {
  [passengerId: string]: PassengerJuniorPilotMap;
}

export interface PassengerJuniorPilotMap {
  serviceStatus?: PassengerJuniorPilotMap.ServiceStatusEnum;
}
/** New Interface Service [junior-pilot]  */
/** ジュニアパイロット
 * @param flightId セグメントIDが項目名となる
 * @param passengerId 搭乗者IDが項目名となる
 */
export interface JuniorPilot {
  [flightId: string]: SegmentJuniorPilotMap;
}

export namespace JuniorPilotInfo {
  export type ServiceStatusEnum = 'requested' | 'unnecessary' | 'notRequested';
  export const ServiceStatusEnum = {
    Requested: 'requested' as ServiceStatusEnum,
    Unnecessary: 'unnecessary' as ServiceStatusEnum,
    NotRequested: 'notRequested' as ServiceStatusEnum,
  };
}
export interface PassengerAirportServiceMap {
  keyword?: string;
}
export interface SegmentAirportServiceMap {
  [travelerId: string]: PassengerAirportServiceMap;
}
/** New Interface Service [asw-inflight-meal]  */
/** アテンド。ANAエアポートサポートのSKコードが登録されている（設定ファイルに定義）ものを返却する
 * @param flightId セグメントIDが項目名となる
 * @param passengerId 搭乗者IDが項目名となる
 * @param keyword SKコード
 */
export interface AirportService {
  [flightId: string]: SegmentAirportServiceMap;
}

export interface PassengerServiceSupportMap {
  specialServiceRequests?: Array<SpecialServiceRequest>;
  serviceStatus?: ServiceSupport.ServiceStatusEnum;
}
/** New Interface Service [support]  */
/** 配慮系サポート。DxAPIより返却された配慮が必要なSSRコードを返却する<br/>
 * (表示するサポート系SSR/SKについて、ASWDBにSSR/SKのリストを保持)
 * @param passengerId 搭乗者IDが項目名となる
 */
export interface ServiceSupport {
  [passengerId: string]: PassengerServiceSupportMap;
}

export namespace ServiceSupport {
  export type ServiceStatusEnum = 'confirmed' | 'requesting' | 'cancelled';
  export const ServiceStatusEnum = {
    Confirmed: 'confirmed' as ServiceStatusEnum,
    Requesting: 'requesting' as ServiceStatusEnum,
    Cancelled: 'cancelled' as ServiceStatusEnum,
  };
}

/** 配慮系サポートSSR情報リスト
   * @param code SSRコード
   * @param statusCode
   * SSRステータスに応じたサービスの申込状態<br/>
      confirmed:申込済<br/>
      HKの場合<br/>
      cancelled：承れなかった<br/>
      UN/NO/UCの場合<br/>
      requesting：リクエスト中<br/>
      HNの場合
   * @param serviceStatus
      当該搭乗者の全SSRステータスに応じたサービスの申込状態<br/>
      confirmed:申込済<br/>
      全てHKの場合<br/>
      cancelled：承れなかった<br/>
      1つでもUN/NO/UCを含む場合<br/>
      requesting：リクエスト中<br/>
      UN/NO/UCを含まないかつ、1つでもHNを含む場合
   */
export interface SpecialServiceRequest {
  code?: string;
  statusCode?: string;
  serviceStatus?: SpecialServiceRequest.ServiceRequestStatusEnum;
}

export namespace SpecialServiceRequest {
  export type ServiceRequestStatusEnum = 'confirmed' | 'requesting' | 'cancelled';
  export const ServiceRequestStatusEnum = {
    Confirmed: 'confirmed' as ServiceRequestStatusEnum,
    Requesting: 'requesting' as ServiceRequestStatusEnum,
    Cancelled: 'cancelled' as ServiceRequestStatusEnum,
  };
}

export interface FlightSeatItem {
  statusCodes?: Array<string>;
  isCrossingMultiplePnrCouchRequested?: boolean;
}

export interface SegmentRegisteredPetsMap {
  [passengerId: string]: Array<PassengerRegisteredPetsMap>;
}
export interface PetService {
  [flightId: string]: SegmentRegisteredPetsMap;
}
export interface RegisteredPets {
  [flightId: string]: RegisteredPets;
}

export interface Cage {
  cageType?: string;
  petType?: string;
  lendingCageType?: Cage.LendingCageTypeEnum;
  size?: Size;
  weight?: number;
}

export namespace Cage {
  export type LendingCageTypeEnum = 'S' | 'M' | 'L' | 'LL';
  export const LendingCageTypeEnum = {
    S: 'S' as LendingCageTypeEnum,
    M: 'M' as LendingCageTypeEnum,
    L: 'L' as LendingCageTypeEnum,
    LL: 'LL' as LendingCageTypeEnum,
  };
}
export interface Size {
  depth?: number;
  width?: number;
  height?: number;
}

export interface PassengerRegisteredPetsMap {
  index?: number;
  id?: string;
  statusCode?: string;
  serviceStatus?: PassengerRegisteredPetsMap.RegisteredPetStatusEnum;
  cage?: Cage;
}

export namespace PassengerRegisteredPetsMap {
  export type RegisteredPetStatusEnum = 'confirmed' | 'requesting' | 'cancelled' | 'requested';
  export const RegisteredPetStatusEnum = {
    Confirmed: 'confirmed' as RegisteredPetStatusEnum,
    Requesting: 'requesting' as RegisteredPetStatusEnum,
    Cancelled: 'cancelled' as RegisteredPetStatusEnum,
    Requested: 'requested' as RegisteredPetStatusEnum,
  };
}

export namespace PassengerJuniorPilotMap {
  export type ServiceStatusEnum = 'requested' | 'unnecessary' | 'notRequested';
  export const ServiceStatusEnum = {
    Requested: 'requested' as ServiceStatusEnum,
    NotRequested: 'notRequested' as ServiceStatusEnum,
    Unnecessary: 'unnecessary' as ServiceStatusEnum,
  };
}
